/**
 * VoucherTable — professional transactions table that renders every voucher
 * type (Receipt / Payment / Sales / Purchase / Journal / Contra) with an
 * inflow/outflow indicator, optional company column and narration.
 * ADDED for the rebuilt dashboard; the older TransactionsTable is left intact.
 */

import React from 'react';
import './VoucherTable.css';
import './DrillDown.css';

const INFLOW_TYPES = new Set(['receipt', 'sales']);

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatAmount = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(Math.abs(Number(amount) || 0));

const VoucherTable = ({ rows = [], loading = false, error = null, showCompany = false, onOpenVoucher = null }) => {
  if (loading) {
    return <div className="vt-state vt-loading">Loading transactions…</div>;
  }
  if (error) {
    return <div className="vt-state vt-error">Failed to load transactions: {error}</div>;
  }
  if (!rows || rows.length === 0) {
    return <div className="vt-state vt-empty">No transactions match the current filters.</div>;
  }

  // Only rows that carry a guid can be drilled into — the guid is the key the
  // /db/companies/:id/voucher/:guid endpoint resolves.
  const canDrill = (r) => Boolean(onOpenVoucher && r.guid);

  return (
    <div className="voucher-table-wrap">
      {onOpenVoucher && (
        <p className="dd-hint">Click any row to see the full voucher with all its ledger entries.</p>
      )}
      <table className="voucher-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Voucher #</th>
            <th>Party</th>
            {showCompany && <th>Company</th>}
            <th className="vt-narration-col">Narration</th>
            <th className="vt-amount-col">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const type = String(r.type || '').toLowerCase();
            const inflow = INFLOW_TYPES.has(type);
            const drillable = canDrill(r);
            return (
              <tr
                key={r.id}
                className={drillable ? 'dd-clickable' : undefined}
                onClick={drillable ? () => onOpenVoucher(r) : undefined}
                title={drillable ? 'Open the full voucher' : undefined}
              >
                <td className="vt-date">{formatDate(r.date)}</td>
                <td>
                  <span className={`vt-badge vt-badge-${type}`}>{r.type}</span>
                </td>
                <td className="vt-voucher">
                  {drillable
                    ? <span className="dd-link">{r.voucherNumber || r.voucherId || '—'}</span>
                    : (r.voucherNumber || r.voucherId || '—')}
                </td>
                <td className="vt-party" title={r.party}>{r.party || '—'}</td>
                {showCompany && (
                  <td className="vt-company" title={r.companyName}>{r.companyName || '—'}</td>
                )}
                <td className="vt-narration" title={r.narration}>{r.narration || '—'}</td>
                <td className={`vt-amount ${inflow ? 'vt-in' : 'vt-out'}`}>
                  <span className="vt-sign">{inflow ? '+' : '−'}</span>
                  {formatAmount(r.amount)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default VoucherTable;
